import * as React from 'react';
import { alpha, Card } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { PickersDay, pickersDayClasses } from '@mui/x-date-pickers/PickersDay';

function WeekHighlightDay(props) {
  const { day, outsideCurrentMonth, selected, ...other } = props;
  const { value, currentView, themeColor } = props; // injected via slotProps.day

  // Only highlight in week view and when we have a valid selected value
  const isWeekView = currentView === 'timeGridWeek';
  const hasValue = !!value?.isValid;

  let inWeek = false,
    isStart = false,
    isEnd = false;

  if (isWeekView && hasValue && day?.isValid) {
    const start = value.startOf('week'); // ISO week (Mon)
    const end = value.endOf('week');

    const t = day.toMillis();
    const t0 = start.startOf('day').toMillis();
    const t1 = end.endOf('day').toMillis();

    inWeek = t >= t0 && t <= t1;
    isStart = day.hasSame(start, 'day');
    isEnd = day.hasSame(end, 'day');
  }

  return (
    <PickersDay
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      selected={selected}
      sx={{
        // soft background for the whole highlighted week
        ...(inWeek &&
          !outsideCurrentMonth && {
            bgcolor: (theme) =>
              (themeColor ?? theme.palette.primary.main) + '22', // ~13% opacity
          }),
        // keep selected day prominent
        ...(selected && {
          bgcolor: (theme) => theme.palette.primary.main + ' !important',
          color: (theme) => theme.palette.primary.contrastText + ' !important',
        }),
        // rounded "pill" ends for the week
        ...(isStart && { borderTopLeftRadius: 16, borderBottomLeftRadius: 16 }),
        ...(isEnd && { borderTopRightRadius: 16, borderBottomRightRadius: 16 }),

        // small aesthetics
        [`&.${pickersDayClasses.outsideCurrentMonth}`]: {
          opacity: 0.5,
        },
      }}
    />
  );
}

export default function StaticDateRangeNavigator({
  value,
  onChange,
  currentView,
}) {
  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}
    >
      <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="da">
        <StaticDatePicker
          value={value}
          views={['year', 'month', 'day']}
          onChange={(newVal) => {
            if (!newVal || !newVal.isValid) return;
            const next = newVal.startOf('day');
            if (!value || !value.hasSame(next, 'day')) onChange(next);
          }}
          displayStaticWrapperAs="desktop"
          slotProps={{
            actionBar: { actions: [] },
            // inject extra props into our custom day component
            day: {
              value,
              currentView,
              themeColor: '#00000015',
            }, // use your green if you like
          }}
          // use a custom day slot to enable week highlighting
          slots={{ day: WeekHighlightDay }}
          sx={{
            '& .MuiDateCalendar-root': { width: '100%' },
            '& .MuiPickersCalendarHeader-labelContainer': {
              fontWeight: 'bold',
              fontSize: '1.2rem',
              textTransform: 'capitalize',
            },
          }}
        />
      </LocalizationProvider>
    </Card>
  );
}
